/**
 * Pilot Technician Training curriculum (MF §14B, task PILOT-TECHNICIAN-TRAINING-001).
 *
 * This module is data plus pure helpers — no React, no API, no authority. It
 * answers two questions for the training surface:
 *
 *  1. which steps of the real pilot workflow should a given user be taught, and
 *  2. which trust states must they be able to recognise.
 *
 * Two rules make it trustworthy rather than decorative:
 *
 *  - Every trust state's label is READ FROM the canonical vocabulary module that
 *    renders it in the product (`statusVocabulary`, `knowledgeGateVocabulary`),
 *    never retyped. §14B requires the training to use "the terminology actually
 *    used in the current UI"; importing the same constant is the only way that
 *    cannot silently drift.
 *  - Every step points at a real route and a real Help Center topic. The
 *    Help-corpus drift guard in `helpTopics.test.js` walks the app and fails if a
 *    `helpTopic` here stops resolving, so training links cannot rot.
 *
 * Nothing here grants permission: `visibleSteps` only ever REMOVES steps for a
 * user who cannot perform them (mirroring Layout's nav rule), so training can
 * never widen what a role may see or do.
 */
import { LIVE_STATUS_META, SOURCE_STATES } from "../statusVocabulary";
import { GATE_DECISIONS, GATE_STATE } from "../knowledgeGateVocabulary";

/**
 * §14B's required trust states, in the order a technician meets them, each tied
 * to the canonical key that drives it in the UI.
 */
export const TRUST_STATES = [
  {
    id: "verified_evidence",
    required: "verified/live evidence",
    label: "ראיה טרייה ומאומתת",
    canonical: `SOURCE_STATES.${SOURCE_STATES.fresh}`,
    explain:
      "הנתון נטען בהצלחה מהמערכת כרגע. זהו המצב שבו מותר להסתמך על התוצאה כעל עובדה.",
  },
  {
    id: "stale_evidence",
    required: "stale/unknown evidence",
    label: "ראיה ישנה",
    canonical: `SOURCE_STATES.${SOURCE_STATES.stale}`,
    explain:
      "הטעינה האחרונה נכשלה אך המסך עדיין מציג תוצאה קודמת. המסך מציין זאת במפורש — אין להתייחס לנתון כאילו הוא עדכני.",
  },
  {
    id: "missing_evidence",
    required: "stale/unknown evidence",
    label: LIVE_STATUS_META.no_evidence.label,
    canonical: `LIVE_STATUS_META.no_evidence`,
    explain:
      "לא נאספה כלל ראיה. היעדר ראיה אינו ראיה שהמצב תקין — זהו מצב שדורש בירור.",
  },
  {
    id: "unknown_state",
    required: "stale/unknown evidence",
    label: LIVE_STATUS_META.unknown.label,
    canonical: `LIVE_STATUS_META.unknown`,
    explain:
      "המערכת אינה יודעת מה המצב. היא מציגה 'לא ידוע' במקום להציג ניחוש.",
  },
  {
    id: "research_mode",
    required: "Research Mode",
    label: "מצב מחקר — אין ניחוש",
    canonical: `GATE_DECISIONS.${GATE_DECISIONS.RESEARCH_REQUIRED}`,
    explain:
      "העובד הדיגיטלי פגש משהו שחבילת המומחה המאושרת אינה מסבירה. הוא עוצר לפני שינוי, מחפש בתיעוד הרשמי של הספק, ורק חומר רשמי שמיישב את אי-ההתאמה משחרר את הפעולה. אין ניחוש.",
  },
  {
    id: "approval_required",
    required: "approval required",
    label: LIVE_STATUS_META.approval_required.label,
    canonical: `LIVE_STATUS_META.approval_required`,
    explain:
      "הפעולה ממתינה לאישור אנושי מפורש. שום סוכן אינו מאשר לעצמו פעולה, והמאשר אינו יכול להיות מי שיזם אותה.",
  },
  {
    id: "blocked_escalated",
    required: "blocked/escalated",
    label: "חסום ומצריך הסלמה",
    canonical: `GATE_STATE.${GATE_STATE.BLOCKED}`,
    explain:
      "הפעולה חסומה. אם התיעוד הרשמי אינו מצליח ליישב את אי-ההתאמה, העובד עוצר ומסלים לאדם — הוא אינו ממשיך בניסיון.",
  },
  {
    id: "stop_escalate",
    required: "blocked/escalated",
    label: "עצירה והסלמה",
    canonical: `GATE_DECISIONS.${GATE_DECISIONS.STOP_ESCALATE}`,
    explain:
      "החלטת השער שהעדויות אינן מספיקות. המסלול היחיד מכאן הוא הסלמה לאדם, לא המשך.",
  },
  {
    id: "verified_postcondition",
    required: "verified postcondition",
    label: "אימות תנאי סיום (postcondition)",
    canonical: "postcondition_read_back",
    explain:
      "לאחר הביצוע המערכת קוראת בחזרה את המצב ומוודאת שמה שהובטח אכן קרה. 'בוצע' אינו מספיק — האימות הוא הראיה.",
  },
];

/** §14B: the AI recommendation is reviewable. These are the four ways out. */
export const REVIEW_ACTIONS = [
  {
    id: "ask_alternative",
    label: "לבקש גישה אחרת",
    explain: "אפשר לבקש מהעובד הדיגיטלי להציע דרך פעולה שונה מזו שהציע.",
  },
  {
    id: "correct_context",
    label: "לתקן את ההקשר",
    explain: "אם הניתוח התבסס על מידע חסר או שגוי, מתקנים את ההקשר והעובד חוזר ומנתח.",
  },
  {
    id: "reject_plan",
    label: "לדחות תוכנית לא בטוחה או שגויה",
    explain: "אפשר לדחות תוכנית שאינה נכונה או שאינה בטוחה. דחייה היא פעולה לגיטימית, לא כשל.",
  },
  {
    id: "escalate",
    label: "להסלים",
    explain: "אם אין ודאות, מסלימים לאדם — כולל כשהעדויות אינן מספיקות כדי להחליט.",
  },
];

/**
 * §14B's pilot path, in order. `screen` is a real route in this app and
 * `helpTopic` a real Help Center topic id.
 *
 * `audience` mirrors Layout's NAV rule and may be STRICTER than the screen's own
 * gate but never looser: a step may require the action's permission on top of the
 * screen's (starting a run needs agents_run even though /tickets is open to all),
 * but it may never omit a permission its screen requires — otherwise training
 * would teach a step about a screen the sidebar hides. `pilotTrainingSteps.test.js`
 * enforces exactly that.
 */
export const STEPS = [
  {
    id: "open_ticket",
    title: "לקבל ולפתוח קריאה",
    screen: "/tickets",
    screenLabel: "קריאות שירות",
    helpTopic: "tickets",
    audience: {},
    what: "נקודת הכניסה של העבודה: קריאה נכנסת מהלקוח או נפתחת ידנית.",
    check: "אתה יודע למצוא קריאה ולפתוח אותה.",
  },
  {
    id: "start_analysis",
    title: "לבקש ניתוח AI",
    screen: "/tickets",
    screenLabel: "קריאות שירות",
    helpTopic: "agents",
    audience: { agentPermissions: ["agents_run"] },
    what: "מבקשים מהעובד הדיגיטלי לנתח את הקריאה. הניתוח הוא הצעה, לא ביצוע.",
    check: "אתה יודע להפעיל ניתוח ולהמתין לו.",
  },
  {
    id: "inspect_evidence",
    title: "לבדוק ראיות ומקור אמת",
    screen: "/agent-runs",
    screenLabel: "ריצות AI",
    helpTopic: "agents",
    audience: { agentPermissions: ["agents_view"] },
    what: "כל מסקנה מגיעה עם מקור. בודקים אם המקור טרי ומאומת או ישן/חסר.",
    check: "אתה מבדיל בין ראיה מאומתת לבין ראיה ישנה או חסרה.",
  },
  {
    id: "review_plan",
    title: "להבין את התוכנית המוצעת",
    screen: "/agent-runs",
    screenLabel: "ריצות AI",
    helpTopic: "approvals",
    audience: { agentPermissions: ["agents_view"] },
    what: "התוכנית מפרטת מה בדיוק יתבצע, על מה, ועם איזה תנאי סיום.",
    check: "אתה יכול להסביר מה הפעולה המוצעת תעשה לפני שהיא מתבצעת.",
  },
  {
    id: "approve_or_correct",
    title: "לאשר, לדחות או לבקש תיקון",
    screen: "/approval-center",
    screenLabel: "מרכז אישורים",
    helpTopic: "approvals",
    audience: { agentPermissions: ["agents_view", "agents_approve"] },
    what: "ההמלצה ניתנת לבדיקה. אפשר לאשר, לדחות תוכנית לא בטוחה, לתקן הקשר או להסלים.",
    check: "אתה יודע לזהות מתי נדרש אישור אנושי ולמי הוא שייך.",
  },
  {
    id: "execution_status",
    title: "לעקוב אחר הביצוע",
    screen: "/agent-runs",
    screenLabel: "ריצות AI",
    helpTopic: "agents",
    audience: { agentPermissions: ["agents_view"] },
    what: "רואים אם הריצה ממתינה לאדם, רצה, הושלמה או נחסמה.",
    check: "אתה יכול לומר במה הריצה עוסקת כרגע.",
  },
  {
    id: "verify_result",
    title: "לבדוק את ראיות האימות",
    screen: "/work-evidence",
    screenLabel: "עבודה וראיות",
    helpTopic: "verification",
    audience: { agentPermissions: ["agents_view"] },
    what: "לאחר הביצוע נבדק תנאי הסיום. 'בוצע' ללא אימות אינו מספיק.",
    check: "אתה מחפש את ראית האימות ולא מסתפק בסטטוס.",
  },
  {
    id: "give_feedback",
    title: "לתת משוב או תיקון",
    screen: "/agent-runs",
    screenLabel: "ריצות AI",
    helpTopic: "agents",
    audience: { agentPermissions: ["agents_view", "agents_run"] },
    what: "תיקון או דירוג של תוצאת ה-AI הוא סיגנל הלמידה של המערכת.",
    check: "אתה יודע לתקן או לדרג תוצאה שקיבלת.",
  },
];

/**
 * §14B's "safe read-only demo exercise": reading trust states without touching
 * production. Every fixture below is a fixed literal — nothing is fetched, no
 * run is created, no ticket is touched, and the panel that renders this is
 * read-only. The exercise teaches the same labels the product shows because the
 * chips read from the canonical vocabulary above, not from retyped strings.
 */
export const DEMO_INTRO =
  "תרגיל קריאה בלבד. ארבעה מצבים לדוגמה, בדיוק כפי שהם מוצגים במערכת. אין כאן קריאה לשרת ואין שינוי בשום מערכת.";

export const DEMO_FIXTURE_NOTE = "נתוני דוגמה קבועים — אינם נטענים מהמערכת";

export const DEMO_STEPS = [
  {
    id: "demo_stale_source",
    title: "מתי נתון הוא ראיה?",
    chip: { label: "ראיה ישנה", canonical: `SOURCE_STATES.${SOURCE_STATES.stale}` },
    fixture: [
      { k: "מסך", v: "עבודה וראיות" },
      { k: "מקור הנתון", v: "טעינה אחרונה נכשלה" },
      { k: "מוצג", v: "תוצאה מהטעינה הקודמת" },
    ],
    prompt: "מה מותר להסיק מהנתון שמוצג?",
    options: [
      "הנתון עדכני ואפשר להסתמך עליו.",
      "הנתון ישן — הטעינה נכשלה, ואסור להתייחס אליו כאל מצב נוכחי.",
      "אין כאן נתון כלל.",
    ],
    correct: 1,
    explain:
      "מסך שמציג תוצאה קודמת אחרי טעינה שנכשלה מסמן את עצמו בפירוש. ראיה ישנה אינה ראיה.",
  },
  {
    id: "demo_research_mode",
    title: "מה קורה כשאין הסבר?",
    chip: {
      label: "מצב מחקר — אין ניחוש",
      canonical: `GATE_DECISIONS.${GATE_DECISIONS.RESEARCH_REQUIRED}`,
    },
    fixture: [
      { k: "תוכנית מאושרת", v: "לא מסבירה שדה שחזר מהמערכת החיצונית" },
      { k: "החלטת השער", v: "נדרש מחקר" },
      { k: "הפעולה", v: "טרם בוצעה" },
    ],
    prompt: "מה קורה לפעולה המאושרת?",
    options: [
      "היא ממשיכה — הרי היא כבר אושרה.",
      "היא מושהית עד שחומר רשמי מהספק יישב את אי-ההתאמה.",
      "היא מבוטלת ואין דרך להמשיך.",
    ],
    correct: 1,
    explain:
      "אישור מכסה את מה שאושר. משהו שאינו מוסבר בתוכנית המאושרת עוצר את הפעולה, ואין ניחוש.",
  },
  {
    id: "demo_approval",
    title: "למי שייך האישור?",
    chip: { label: LIVE_STATUS_META.approval_required.label, canonical: "LIVE_STATUS_META.approval_required" },
    fixture: [
      { k: "הפעולה", v: "שינוי מצב במערכת החיצונית" },
      { k: "יוזם הריצה", v: "המשתמש שאתה" },
      { k: "סטטוס", v: "ממתין לאישור אנושי" },
    ],
    prompt: "מי יכול לאשר את הפעולה?",
    options: [
      "המשתמש שיזם את הריצה — הוא כבר מכיר את המקרה.",
      "משתמש אחר עם הרשאת אישור; אין אישור עצמי.",
      "הסוכן שהציע את הפעולה.",
    ],
    correct: 1,
    explain:
      "אין אישור עצמי ואין סוכן שמאשר את הצעת עצמו. האישור חייב לבוא מאדם אחר עם ההרשאה.",
  },
  {
    id: "demo_postcondition",
    title: "מתי ביצוע נחשב מוצלח?",
    chip: {
      label: "אימות תנאי סיום (postcondition)",
      canonical: "postcondition_read_back",
    },
    fixture: [
      { k: "הביצוע", v: "הסתיים" },
      { k: "אימות תנאי סיום", v: "לא תאם את המצב הצפוי" },
      { k: "ראיה", v: "קריאה חוזרת מהמערכת" },
    ],
    prompt: "מהי המסקנה?",
    options: [
      "הביצוע הושלם בהצלחה — הוא הסתיים.",
      "הביצוע לא אומת, ולכן אינו נחשב מוצלח עד שיוסבר הפער.",
      "צריך לאשר את הפעולה מחדש.",
    ],
    correct: 1,
    explain:
      "הסטטוס 'הסתיים' אינו הראיה. הראיה היא קריאה חוזרת שמאשרת שהמצב הרצוי התקבל בפועל.",
  },
];

/** Layout's nav rule, reused so a step is visible exactly when its screen is. */
export function canSeeStep(step, { isAdmin = false, canAgent = () => false } = {}) {
  const a = (step && step.audience) || {};
  const required = stepPermissions(step);
  if (required.length > 0) return required.every((p) => Boolean(canAgent(p)));
  if (a.adminOnly) return Boolean(isAdmin);
  return true;
}

/** Normalised permission requirements for a step (possibly empty). */
export function stepPermissions(step) {
  const a = (step && step.audience) || {};
  if (Array.isArray(a.agentPermissions)) return a.agentPermissions;
  return a.agentPermission ? [a.agentPermission] : [];
}

export function visibleSteps(audience) {
  return STEPS.filter((s) => canSeeStep(s, audience));
}

export const EMPTY_PROGRESS = { status: "new", done: {}, demoDone: false };

/**
 * Progress is reported against the steps THIS user can see, so a technician is
 * never shown "80%" for steps they are not permitted to perform.
 */
export function trainingProgress(progress, audience) {
  const steps = visibleSteps(audience);
  const done = (progress && progress.done) || {};
  const completedSteps = steps.filter((s) => done[s.id]).length;
  const total = steps.length;
  return {
    steps,
    completedSteps,
    total,
    allStepsDone: total > 0 && completedSteps === total,
    demoDone: Boolean(progress && progress.demoDone),
  };
}
